import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Avatar,
  Badge,
  Container,
  CircularProgress,
  IconButton,
  Slide,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ArrowForwardIos, ExpandMore, ExpandLess } from "@mui/icons-material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import Logo from "../../../Assets/Images/Logo.png";
import ActionsMenu from "../../../components/ActionsMenu";
import LoadingToast from "../../../components/LoadingToast";
import AddCustomPolicyFlow from "./PolicyDialogs/AddCustomPolicyFlow";
import SetupPolicyDialog from "./PolicyDialogs/SetupPolicyDialog";
import { useMutation, useQuery } from "@apollo/client";
import {
  GET_POLICY_LIST,
  UPDATE_POLICY_STATUS,
  GENERATE_POLICY_DOWNLOAD_SIGNED_URL,
} from "../../../apis/settingsApis/policy-apis/policyApis";
import { GET_ROLES } from "../../../apis/dashboard-apis/dashboardDetailApi";
import ConfirmSnackbar from "../../../components/Snackbar/ConfirmSnackbar";
import RejectPolicyDialog from "./PolicyDialogs/RejectPolicyDialog";
import { useNavigate } from "react-router-dom";
import { CheckStatusChip } from "../../../components/CheckStatusChip/CheckStatusChip";
import { CheckStatus } from "../../../utils/enums";
import InformationStepsCard from "../../../components/InformationStepsCard";
import { FEATURE_IDS } from "../../../components/AccessControl";

const PolicyList = () => {
  const theme = useTheme();
  const { loading, data, refetch } = useQuery(GET_POLICY_LIST);
  const [updatePolicyStatusByUuid, { loading: statusLoading }] =
    useMutation(UPDATE_POLICY_STATUS);
  const [generatePolicyDownloadSignedUrl] = useMutation(GENERATE_POLICY_DOWNLOAD_SIGNED_URL);
  const { data: dashboardData, refetch: refetchDashboard } =
    useQuery(GET_ROLES);
  const [showAll, setShowAll] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const [policyData, setPolicyData] = useState({});
  const [isDownloadingPolicies, setIsDownloadingPolicies] = useState(false);
  const [isAddCustomPolicyFlowOpen, setIsAddCustomPolicyFlowOpen] = useState(false);

  const activePolicies = data?.getAllTypePolicies.activePolicies || [];
  const pendingPolicies = data?.getAllTypePolicies.pendingPolicies || [];
  const draftPolicies = data?.getAllTypePolicies.draftPolicies || [];
  const policiesList = data?.getAllTypePolicies.policiesList || [];

  // Create a set of excluded policy IDs
  const excludedPolicyIds = new Set([
    ...draftPolicies.map((policy) => policy.policyId),
    ...activePolicies.map((policy) => policy.policyId),
    ...pendingPolicies.map((policy) => policy.policyId),
  ]);

  // Filter policies to exclude those in draft, active, or pending
  const filteredPolicies = policiesList.filter(
    (policy) => !excludedPolicyIds.has(policy.policyId)
  );

  // Define visiblePolicies by excluding the policies in draft, active, or pending
  const visiblePolicies = showAll
    ? filteredPolicies
    : filteredPolicies.slice(0, 3);

  useEffect(() => {
    refetch();
    refetchDashboard();
  }, [refetch, refetchDashboard]);

  // console.log("requiredPolicies", policyData);
  const TransitionComponent = (props) => <Slide {...props} direction="left" />;

  const handleUpdateStatus = async (file_uuid, status, reason) => {
    try {
      console.log("reason", reason);
      const response = await updatePolicyStatusByUuid({
        variables: {
          file_uuid: String(file_uuid),
          status,
          ...(reason && { reason }),
        },
      });
      setSnackbarInfo({
        message: response?.data?.updatePolicyStatusByUuid?.message,
        vertical: "top",
        horizontal: "right",
        Transition: TransitionComponent,
      });
    } catch (error) {
      console.error("Error:", error);
      setSnackbarInfo({
        message: error?.message,
        vertical: "top",
        horizontal: "right",
        Transition: TransitionComponent,
      });
    }
  };

  if (loading) {
    return (
      <Container
        component="main"
        maxWidth="xs"
        sx={{ textAlign: "center", mt: 8 }}
      >
        <CircularProgress />
      </Container>
    );
  }

  const handleAddCustomPolicy = () => {
    setIsAddCustomPolicyFlowOpen(true);
  };

  const handleDownloadPolicies = async () => {
    setIsDownloadingPolicies(true);
    try {
      const result = await generatePolicyDownloadSignedUrl();
      const url = result?.data?.generatePolicyDownloadSignedUrl?.url;
      if (!url) {
        setSnackbarInfo({
          message: "Download failed: no URL returned",
          vertical: "top",
          horizontal: "right",
          Transition: TransitionComponent,
        });
        return;
      }
      const link = document.createElement("a");
      link.href = url;
      link.download = "policies";
      link.rel = "noopener noreferrer";
      link.click();
    } catch (err) {
      setSnackbarInfo({
        message: "Download failed. Please try again.",
        vertical: "top",
        horizontal: "right",
        Transition: TransitionComponent,
      });
    } finally {
      setIsDownloadingPolicies(false);
    }
  };

  const policyListMenuItems = [
    { label: "Add custom policy", onClick: handleAddCustomPolicy, featureId: FEATURE_IDS.POLICIES_LIST_ADD_CUSTOM_POLICY },
    { label: "Download policies", onClick: handleDownloadPolicies, featureId: FEATURE_IDS.POLICIES_LIST_DOWNLOAD_ALL_POLICIES },
  ];

  return (
    <Box sx={{ paddingTop: "20px" }}>
      {/* Information Steps Card */}
      <InformationStepsCard
        heading="**What should I do on this page?**"
        subtitle="This page shows all policies required by your selected compliance frameworks (e.g., **SOC 2**, **ISO 27001**)."
        steps={[
          "This page helps you manage all company policies required for compliance frameworks like **SOC 2** or **ISO 27001**. You can create, upload, and track them through their lifecycle.",

          "**Required Policies and Documents** show the policies your organization needs to upload. Review the list of policies and ensure each one is set up and ready for review.",

          "**Draft Policies** are policies you’ve created but not yet sent for approval. Review them and submit for approval when ready.",

          "**Pending for Approval** lists policies waiting for another admin’s review. This follows the maker-checker approval process.",

          "**Active Policies** are approved and published. Employees can now view and acknowledge them from their portal.",

          "Once all employees acknowledge a policy, it moves to the **Completed** state, helping your organization stay compliant and audit-ready.",
        ]}
        footerText="💡 Track employee acknowledgment on the [People page](/people) to ensure everyone has accepted your policies."
        collapsible={true}
        defaultExpanded={false}
      />

      {/* Introduction Section: description + page actions */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 2.5,
          flexWrap: "wrap",
        }}
      >
        <Typography
          variant="body1"
          sx={{ color: "text.secondary", flex: "1 1 300px" }}
        >
          Policies and related documents are an essential part of your security
          program. These include rules for governing specific operational areas
          and procedures to operate in alignment with those rules.
        </Typography>
        <Box sx={{ flexShrink: 0 }}>
          <ActionsMenu
            ariaLabel="Policy actions"
            items={policyListMenuItems}
          />
        </Box>
      </Box>

      {/* Draft Policies Section */}
      <PoliciesSection
        title="Draft Policies"
        policies={draftPolicies}
        setIsOpen={setIsOpen}
        setPolicyData={setPolicyData}
        showSetupButton={false}
        dashboardData={dashboardData}
        showVersion={true}
        handleUpdateStatus={handleUpdateStatus}
        statusLoading={statusLoading}
        refetch={refetch}
      />

      {/* Pending Policies Section */}
      <PoliciesSection
        title="Pending for Approval"
        policies={pendingPolicies}
        setIsOpen={setIsOpen}
        setPolicyData={setPolicyData}
        showSetupButton={false}
        dashboardData={dashboardData}
        showVersion={true}
        handleUpdateStatus={handleUpdateStatus}
        statusLoading={statusLoading}
        refetch={refetch}
      />

      {/* Active Policies Section */}
      <PoliciesSection
        title="Active Policies"
        policies={activePolicies}
        setIsOpen={setIsOpen}
        setPolicyData={setPolicyData}
        showSetupButton={false}
        dashboardData={dashboardData}
        showVersion={true}
        handleUpdateStatus={handleUpdateStatus}
        statusLoading={statusLoading}
        refetch={refetch}
      />

      {/* Required Policies Section */}
      {filteredPolicies.length > 0 && (
        <>
          <Typography variant="h4" gutterBottom fontWeight="bold" mt={3}>
            Required Policies and Documents ({filteredPolicies.length})
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", marginBottom: "20px" }}
          >
            Explore Regodit's curated recommendation based on your chosen
            frameworks.
          </Typography>

          <Grid container spacing={2}>
            {visiblePolicies.map((policy, index) => (
              <Grid item xs={12} key={index}>
                <PolicyCard
                  policy={policy}
                  setIsOpen={setIsOpen}
                  setPolicyData={setPolicyData}
                  showSetupButton={true}
                  dashboardData={dashboardData}
                  showVersion={false}
                  handleUpdateStatus={handleUpdateStatus}
                  statusLoading={statusLoading}
                  refetch={refetch}
                />
              </Grid>
            ))}
          </Grid>

          <Box textAlign="center" mt={2}>
            <Button
              onClick={() => setShowAll(!showAll)}
              endIcon={showAll ? <ExpandLess /> : <ExpandMore />}
              sx={{
                textTransform: "none",
                color: "primary.main",
                fontWeight: "bold",
              }}
            >
              {showAll ? "Show Less" : "Show All & Setup"}
            </Button>
          </Box>
        </>
      )}

      {/* Policy Dialog */}
      <SetupPolicyDialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        policyData={policyData}
        refetch={refetch}
        version={1}
        isCreate={true}
      />

      <AddCustomPolicyFlow
        open={isAddCustomPolicyFlowOpen}
        onClose={() => setIsAddCustomPolicyFlowOpen(false)}
        refetch={refetch}
        setSnackbarInfo={setSnackbarInfo}
        snackbarTransition={TransitionComponent}
      />

      <LoadingToast
        open={isDownloadingPolicies}
        title="Preparing your download"
        message="Sit back and relax — this may take a few moments."
      />

      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </Box>
  );
};

const PoliciesSection = ({
  title,
  policies,
  setIsOpen,
  setPolicyData,
  showSetupButton,
  dashboardData,
  showVersion,
  handleUpdateStatus,
  statusLoading,
  refetch,
}) => {
  if (policies.length === 0) return null;

  return (
    <Box mt={4}>
      <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
        {title} ({policies.length})
      </Typography>
      <Grid container spacing={2}>
        {policies.map((policy, index) => (
          <Grid item xs={12} key={index}>
            <PolicyCard
              policy={policy}
              setIsOpen={setIsOpen}
              setPolicyData={setPolicyData}
              showSetupButton={showSetupButton}
              dashboardData={dashboardData}
              showVersion={showVersion}
              handleUpdateStatus={handleUpdateStatus}
              statusLoading={statusLoading}
              refetch={refetch}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

const PolicyCard = ({
  policy,
  setIsOpen,
  setPolicyData,
  showSetupButton,
  dashboardData,
  showVersion,
  handleUpdateStatus,
  statusLoading,
  refetch,
}) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [open, setOpen] = useState(false);

  const formattedTitle = policy?.policyDisplayName
    ?.replace(/[\s\/]+/g, "-")
    .toLowerCase();

  const roles = dashboardData?.getRoles;
  const approverId = policy?.approver?.role_id;
  const ownerId = policy?.owner?.role_id;

  const handleSendForApproval = async () => {
    setIsLoading(true);
    try {
      await handleUpdateStatus(policy?.fileUuid, "pending approval", null);
      refetch();
    } catch (error) {
      console.error("Error sending for approval:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReject = async (reason) => {
    setIsRejecting(true);
    try {
      await handleUpdateStatus(policy?.fileUuid, "reject", reason);
      refetch();
      setOpen(false);
    } catch (error) {
      console.error("Error rejecting:", error);
    } finally {
      setIsRejecting(false);
    }
  };

  const handleApprove = async () => {
    setIsApproving(true);
    try {
      await handleUpdateStatus(policy?.fileUuid, "active", null);
      refetch();
    } catch (error) {
      console.error("Error approving:", error);
    }
    setIsApproving(false);
  };

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: "12px",
        padding: "10px",
        border: "1px dashed #C5C9CF",
        "&:hover": { background: "#F7F7FC" },
      }}
    >
      <CardContent
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            badgeContent={
              <Avatar
                sx={{
                  width: 20,
                  height: 20,
                  bgcolor: "primary.dark",
                  padding: "2px",
                }}
                src={Logo}
              />
            }
          >
            <Avatar
              sx={{
                bgcolor: "#F7F7FC",
                width: 48,
                height: 48,
                border: "1px solid #E0E0E0",
              }}
            >
              <DescriptionOutlinedIcon sx={{ color: "primary.main" }} />
            </Avatar>
          </Badge>
          <Box ml={2}>
            <Typography
              variant="body1"
              display="flex"
              alignItems="baseline"
              gap="8px"
              sx={{ fontSize: "18px" }}
            >
              {policy.policyDisplayName}
              {showVersion && (
                <Chip
                  label={
                    policy?.fileStatus === "draft" ||
                      policy?.fileStatus === "pending approval"
                      ? `Draft:v${policy?.fileVersion}`
                      : policy?.fileStatus === "reject"
                        ? `Rejected Draft:v${policy?.fileVersion}`
                        : `Active:v${policy?.fileVersion}`
                  }
                  size="small"
                  variant="outlined"
                  sx={{
                    bgcolor:
                      policy?.fileStatus === "draft" ||
                        policy?.fileStatus === "pending approval"
                        ? "#f0f4ff"
                        : policy?.fileStatus === "reject"
                          ? "#FEEBEB"
                          : "#E8FBF3",
                    color:
                      policy?.fileStatus === "draft" ||
                        policy?.fileStatus === "pending approval"
                        ? "inherit"
                        : policy?.fileStatus === "reject"
                          ? "#C71414"
                          : "#057F28",
                    borderColor:
                      policy?.fileStatus === "draft" ||
                        policy?.fileStatus === "pending approval"
                        ? ""
                        : policy?.fileStatus === "reject"
                          ? "#C71414"
                          : "#057F28",
                  }}
                />
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {policy.category === "isms document"
                ? "ISMS Document"
                : policy?.category?.charAt(0)?.toUpperCase() +
                policy?.category?.slice(1)}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {/** removing the below lines since these don't seem to provide any value for now, and were cluttering UI */}
          {/* <Chip
            label={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography variant="body2" sx={{ marginRight: "4px" }}>
                  Controls
                </Typography>
                <BootstrapTooltip
                  title={
                    <>
                      {policy?.fileStatus
                        ? policy?.controls?.map((control, index) => (
                            <Typography
                              key={index}
                              variant="p"
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "6px",
                              }}
                            >
                              {`${index + 1}. ${control?.controlsName}`}
                            </Typography>
                          ))
                        : policy?.controlsName?.map((control, index) => (
                            <Typography
                              key={index}
                              variant="p"
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "6px",
                              }}
                            >
                              {`${index + 1}. ${control}`}
                            </Typography>
                          ))}
                    </>
                  }
                  arrow
                  placement="right-start"
                >
                  <Avatar
                    sx={{
                      bgcolor: "#C9DFF9",
                      color: "#031B89",
                      width: 24,
                      height: 24,
                      fontSize: "0.8rem",
                    }}
                  >
                    {policy?.fileStatus
                      ? policy?.controls?.length
                      : policy?.controlsName?.length}
                  </Avatar>
                </BootstrapTooltip>
              </Box>
            }
            variant="outlined"
            sx={{
              marginRight: "8px",
              "& .MuiChip-label": { padding: "0 6px" },
            }}
          />
          <Chip
            label={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="body2" sx={{ marginRight: "4px" }}>
                  Frameworks
                </Typography>
                <BootstrapTooltip
                  title={
                    <>
                      {policy?.frameworkName.map((framework, index) => (
                        <Typography
                          key={index}
                          variant="p"
                          sx={{ display: "block", marginBottom: "4px" }}
                        >
                          {`${index + 1}. ${framework}`}
                        </Typography>
                      ))}
                    </>
                  }
                  arrow
                  placement="right-start"
                >
                  <Avatar
                    sx={{
                      bgcolor: "#C9DFF9",
                      color: "#031B89",
                      width: 24,
                      height: 24,
                      fontSize: "0.8rem",
                    }}
                  >
                    {policy?.frameworkName?.length}
                  </Avatar>
                </BootstrapTooltip>
              </Box>
            }
            variant="outlined"
            sx={{
              marginRight: "16px",
              "& .MuiChip-label": { padding: "0 6px" },
            }}
          /> */}
          {showSetupButton ? (
            <Button
              endIcon={<ArrowForwardIos />}
              sx={{
                textTransform: "none",
                color: "primary.main",
                fontWeight: "bold",
              }}
              onClick={() => {
                setIsOpen(true);
                setPolicyData(policy);
              }}
            >
              Set Up
            </Button>
          ) : (
            <Box display="flex" alignItems="center">
              <Box display="flex" gap={1}>
                {policy?.passingChecksCount > 0 && (
                  <CheckStatusChip
                    status={CheckStatus.PASS}
                    count={policy?.passingChecksCount}
                  />
                )}
                {policy?.failingChecksCount > 0 && (
                  <CheckStatusChip
                    status={CheckStatus.FAIL}
                    count={policy?.failingChecksCount}
                  />
                )}
                {policy?.disabledChecksCount > 0 && (
                  <CheckStatusChip
                    status={CheckStatus.DISABLED}
                    count={policy?.disabledChecksCount}
                  />
                )}
                {policy?.pendingChecksCount > 0 && (
                  <CheckStatusChip
                    status={CheckStatus.PENDING}
                    count={policy?.pendingChecksCount}
                  />
                )}
              </Box>

              <IconButton
                sx={{
                  "&:hover": { background: "none" },
                  marginLeft: "20px",
                }}
                onClick={() => {
                  navigate(
                    `/policy/detail/${formattedTitle}?id=${policy?.policyId}`
                  );
                }}
              >
                <ArrowForwardIos color="primary" fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
      </CardContent>
      <RejectPolicyDialog
        open={open}
        onClose={() => setOpen(false)}
        onReject={handleReject}
        policyName={`${policy?.policyDisplayName}: v${policy?.fileVersion}`}
        isRejecting={isRejecting}
      />
    </Card>
  );
};

export default PolicyList;
